import React, { useContext, useState, useEffect } from 'react';
import {
    Button, Modal, Row, Card, Col,
    Tooltip, Spin, Input, List, Avatar, Select, message
} from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import CustomerProductContext from '../../ContextFiles/CustomerProductContext';
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';

const PERSPECTIVE_RENDER_FOLDER = 'product_assets/perspective_renders';
const isProductionEnv = (ENVIRONMENT.ENV || '').toLowerCase() === 'production';
const PERSPECTIVE_BASE_BUCKET = isProductionEnv
    ? 'https://all3d.s3.us-west-2.amazonaws.com'
    : 'https://all3d-dev.s3.us-west-2.amazonaws.com';
const PERSPECTIVE_BASE_URL = `${PERSPECTIVE_BASE_BUCKET}/${PERSPECTIVE_RENDER_FOLDER}`;

const normalizePerspectiveImageUrl = (url) => {
    if (!url) return '';

    if (url.startsWith(PERSPECTIVE_BASE_URL)) {
        return url;
    }

    const marker = `/${PERSPECTIVE_RENDER_FOLDER}/`;
    const markerIndex = url.indexOf(marker);
    if (markerIndex === -1) {
        return url;
    }

    const suffix = url.substring(markerIndex + marker.length).replace(/^\/+/, '');
    if (!suffix) {
        return url;
    }

    return `${PERSPECTIVE_BASE_URL}/${suffix}`;
};

const CUSTOMER_USERNAME = localStorage.getItem('username');

const { Option } = Select;
const IMAGE_SLOT_OPTIONS = [
    { value: 'main', label: 'MAIN', order: 0 },
    { value: 'pt01', label: 'Position1', order: 1 },
    { value: 'pt02', label: 'Position2', order: 2 },
    { value: 'pt03', label: 'Position3', order: 3 },
    { value: 'pt04', label: 'Position4', order: 4 },
    { value: 'pt05', label: 'Position5', order: 5 },
    { value: 'pt06', label: 'Position6', order: 6 },
    { value: 'pt07', label: 'Position7', order: 7 },
    { value: 'pt08', label: 'Position8', order: 8 },
];

const IMAGE_SLOT_MAP = IMAGE_SLOT_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option;
    return acc;
}, {});

const getSlotMetadata = (slot) => {
    if (!slot) {
        return null;
    }
    return IMAGE_SLOT_MAP[slot] || null;
};

const ImageListTypePreviews = ({
    product_images = [],
    selectedImages = [],
    toggleImageSelection,
    onSlotChange,
    occupiedSlots = []
}) => {
    const validImages = product_images.filter(image => image && (image.low_url || image.url));

    if (!validImages.length) {
        return <div className="manrope f-14 w-600 text-center w-100">No images available for selection</div>;
    }

    return (
        <>
            {validImages.map((image, index) => {
                const selection = selectedImages.find((item) => item.url === image.url);
                const isSelected = Boolean(selection);
                const thumbnail = image.low_url || image.url;

                return (
                    <Card
                        className={`product-img-grid-card comparison-modal display-flex ${isSelected ? 'selected' : ''}`}
                        key={index}
                        onClick={() => toggleImageSelection(image.url)}
                        style={{ position: 'relative' }}
                    >
                        {isSelected && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    backgroundColor: '#262626',
                                    color: '#ffffff',
                                    borderRadius: 4,
                                    padding: '1px 6px',
                                    fontSize: 12,
                                    fontWeight: 600
                                }}
                            >
                                {selection?.slot ? (IMAGE_SLOT_MAP[selection.slot]?.label || '—') : '?'}
                            </div>
                        )}
                        <img
                            className='product-img-grid-img'
                            src={thumbnail}
                            alt={image.display_name || image.filename || 'Product image'}
                            onError={(event) => {
                                event.currentTarget.style.visibility = 'hidden';
                            }}
                        />
                        <Select
                            allowClear
                            placeholder="Select image part"
                            value={selection?.slot}
                            className="manrope f-12 black-00 select-option-dropdown"
                            style={{ marginTop: 8 }}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(value) => onSlotChange(image.url, value ?? null)}
                        >
                            {IMAGE_SLOT_OPTIONS.map((option) => (
                                <Option
                                    key={option.value}
                                    value={option.value}
                                    disabled={occupiedSlots.includes(option.value) && selection?.slot !== option.value}
                                    className="manrope f-12 select-view"
                                >
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Card>
                );
            })}
        </>
    );
};

const ShopifyImageSelectionModal = ({ visible, onCancel, productId }) => {
    const { allProductImages, productAssetData, product_id } = useContext(CustomerProductContext);
    const [selectedImagesForUpload, setSelectedImagesForUpload] = useState([]);

    // Product lookup state (reused from ShopifyUploadModal)
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState('');
    const [autoMatchedProduct, setAutoMatchedProduct] = useState(null);
    const [manualSearchTerm, setManualSearchTerm] = useState('');
    const [manualSearchLoading, setManualSearchLoading] = useState(false);
    const [manualResults, setManualResults] = useState([]);
    const [selectedShopifyProduct, setSelectedShopifyProduct] = useState(null);
    const [productIdentifiers, setProductIdentifiers] = useState({ sku: null, upc: null });
    const [hasSearched, setHasSearched] = useState(false);

    // Upload state
    const [shopifyUploadError, setShopifyUploadError] = useState('');
    const [uploadingToShopify, setUploadingToShopify] = useState(false);

    const targetProductId = productId || product_id;
    const matchedProduct = selectedShopifyProduct || autoMatchedProduct;

    const getNextAvailableSlot = (selections) => {
        const used = new Set(
            selections
                .map((item) => item.slot)
                .filter(Boolean)
        );
        return IMAGE_SLOT_OPTIONS.find((option) => !used.has(option.value)) || null;
    };

    const toggleImageSelection = (url) => {
        setSelectedImagesForUpload((prev) => {
            const existing = prev.find((item) => item.url === url);
            if (existing) {
                const updated = prev.filter((item) => item.url !== url);
                if (!updated.length) {
                    setShopifyUploadError('');
                }
                return updated;
            }

            if (prev.length >= IMAGE_SLOT_OPTIONS.length) {
                setShopifyUploadError(`You can select up to ${IMAGE_SLOT_OPTIONS.length} images.`);
                return prev;
            }

            const nextSlot = getNextAvailableSlot(prev);
            setShopifyUploadError('');
            return [
                ...prev,
                {
                    url,
                    slot: nextSlot?.value ?? null,
                    order: nextSlot?.order ?? null
                }
            ];
        });
    };

    const updateImageSlot = (url, slotValue) => {
        const slotMeta = slotValue ? getSlotMetadata(slotValue) : null;

        setSelectedImagesForUpload((prev) => {
            const withoutTarget = prev.filter((item) => item.url !== url);

            let updated = withoutTarget
                .map((item) => {
                    if (slotMeta && item.slot === slotMeta.value) {
                        return { ...item, slot: null, order: null };
                    }
                    return item;
                })
                .filter((item) => item.slot);

            if (slotMeta) {
                updated.push({
                    url,
                    slot: slotMeta.value,
                    order: slotMeta.order
                });
            }

            return updated;
        });

        setShopifyUploadError('');
    };

    const orderedImageSelections = selectedImagesForUpload
        .filter((item) => typeof item.order === 'number')
        .sort((a, b) => a.order - b.order);

    const occupiedSlots = selectedImagesForUpload
        .map((item) => item.slot)
        .filter(Boolean);

    const cancelSelectedComparisonImageModal = () => {
        onCancel();
        setSelectedImagesForUpload([]);
        resetState();
    }

    const resetState = () => {
        setLookupLoading(false);
        setLookupError('');
        setAutoMatchedProduct(null);
        setManualSearchTerm('');
        setManualResults([]);
        setManualSearchLoading(false);
        setSelectedShopifyProduct(null);
        setHasSearched(false);
        setShopifyUploadError('');
        setSelectedImagesForUpload([]);
    }

    const ensureProductIdentifiersThenLookup = async (useProductId = null) => {
        const targetProductId = useProductId || product_id;
        if (!targetProductId) return;
        try {
            const hasValidSku = productIdentifiers.sku && productIdentifiers.sku.trim();
            const hasValidUpc = productIdentifiers.upc && productIdentifiers.upc.trim();
            if (hasValidSku || hasValidUpc) {
                await autoLookupShopifyProduct();
                return;
            }
            let sku = (productAssetData?.brand_id && productAssetData.brand_id.trim()) ? productAssetData.brand_id : null;
            let upc = (productAssetData?.gtin && productAssetData.gtin.trim()) ? productAssetData.gtin : null;

            if (!sku && !upc) {
                try {
                    const response = await axios.post(ENVIRONMENT.FETCH_PRODUCT, {
                        product_id: targetProductId,
                        username: CUSTOMER_USERNAME
                    });
                    const data = response.data;
                    const payload = data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
                    const fetchedSku = (payload?.brand_id && String(payload.brand_id).trim()) ? String(payload.brand_id).trim() : null;
                    const fetchedUpc = (payload?.gtin && String(payload.gtin).trim()) ? String(payload.gtin).trim() : null;
                    if (fetchedSku) sku = fetchedSku;
                    if (fetchedUpc) upc = fetchedUpc;
                } catch (e) {
                    // fall through
                }
            }
            setProductIdentifiers({ sku, upc });
        } catch (e) {
            // fall through
        } finally {
            await autoLookupShopifyProduct();
        }
    };

    const autoLookupShopifyProduct = async () => {
        try {
            setLookupLoading(true);
            setLookupError('');
            setAutoMatchedProduct(null);
            setSelectedShopifyProduct(null);
            
            const rawSku = productIdentifiers.sku ?? productAssetData?.brand_id;
            const rawUpc = productIdentifiers.upc ?? productAssetData?.gtin;
            const sku = (rawSku && String(rawSku).trim()) ? String(rawSku).trim() : null;
            const upc = (rawUpc && String(rawUpc).trim()) ? String(rawUpc).trim() : null;

            if (!sku && !upc) {
                setLookupLoading(false);
                setLookupError('No SKU/UPC found on this item. Please search by title.');
                return;
            }

            try {
                const response = await axios.post(ENVIRONMENT.SHOPIFY_FIND_PRODUCT, {
                    username: CUSTOMER_USERNAME,
                    sku: sku || undefined,
                    upc: upc || undefined,
                });
                const data = response.data;
                const rawData = data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
                const match = Array.isArray(rawData?.products) ? rawData.products[0] : (rawData?.product || (rawData?.id ? rawData : null));
                
                if (match && match.id) {
                    setAutoMatchedProduct(match);
                    try {
                        const directSku = (match?.sku && String(match.sku).trim()) ? String(match.sku).trim() : null;
                        const directUpc = ((match?.upc && String(match.upc).trim()) || (match?.barcode && String(match.barcode).trim()))
                            ? String(match.upc || match.barcode).trim() : null;
                        const firstVariant = match?.variants?.edges?.[0]?.node || match?.variants?.[0] || null;
                        const variantSku = (firstVariant?.sku && String(firstVariant.sku).trim()) ? String(firstVariant.sku).trim() : null;
                        const variantUpc = (firstVariant?.barcode && String(firstVariant.barcode).trim()) ? String(firstVariant.barcode).trim() : null;
                        setProductIdentifiers({
                            sku: directSku || variantSku || null,
                            upc: directUpc || variantUpc || null,
                        });
                    } catch (e) {
                        // ignore parsing issues
                    }
                    setLookupError('');
                } else {
                    setLookupError('No matching product found by SKU/UPC. Please search by title.');
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Lookup failed. Please search by title.';
                setLookupError(errorMessage);
            }
        } catch (e) {
            setLookupError('Lookup failed. Please search by title.');
        } finally {
            setLookupLoading(false);
        }
    };

    const handleManualSearch = async () => {
        if (!manualSearchTerm || manualSearchTerm.length < 2) {
            return;
        }
        setHasSearched(true);
        try {
            setManualSearchLoading(true);
            setManualResults([]);
            const response = await axios.post(ENVIRONMENT.SHOPIFY_SEARCH_PRODUCTS, {
                username: CUSTOMER_USERNAME,
                query: manualSearchTerm,
            });
            const data = response.data;
            const products = data?.body?.products || data?.products || [];
            setManualResults(products);
        } catch (e) {
            setManualResults([]);
        } finally {
            setManualSearchLoading(false);
        }
    };

    const uploadToShopify = async () => {
        if (!matchedProduct || !matchedProduct.id) {
            setShopifyUploadError('Please select a Shopify product first.');
            return;
        }
        if (selectedImagesForUpload.length === 0) {
            setShopifyUploadError('Please select at least one image to upload.');
            return;
        }

        if (orderedImageSelections.length !== selectedImagesForUpload.length) {
            setShopifyUploadError('Assign a unique image part to every selected image.');
            return;
        }

        setUploadingToShopify(true);
        setShopifyUploadError('');

        try {
            const imagePayload = orderedImageSelections
                .map((item) => {
                    const normalized = normalizePerspectiveImageUrl(item.url);
                    if (!normalized) {
                        return null;
                    }
                    return {
                        image_url: normalized,
                        slot: item.slot,
                        order: item.order
                    };
                })
                .filter(Boolean);

            const uploadImages = imagePayload.map((item) => item.image_url);

            if (!uploadImages.length) {
                setShopifyUploadError('Unable to resolve image URLs for upload.');
                return;
            }

            const response = await axios.post(ENVIRONMENT.SHOPIFY_BULK_UPLOAD_IMAGES, {
                username: CUSTOMER_USERNAME,
                product_id: targetProductId,
                shopify_product_id: matchedProduct.id,
                image_urls: uploadImages,
                image_slots: imagePayload
            });

            const { status } = response;
            const rawPayload = response?.data?.body ?? response?.data ?? {};
            let parsedPayload = rawPayload;
            if (typeof rawPayload === 'string') {
                try {
                    parsedPayload = JSON.parse(rawPayload);
                } catch (e) {
                    parsedPayload = { message: rawPayload };
                }
            }
            const apiSuccess = parsedPayload?.success;
            const apiMessage = parsedPayload?.message || response?.data?.message;
            const didSucceed = apiSuccess === undefined || apiSuccess === true || apiSuccess === 'true';

            if (status >= 200 && status < 300 && didSucceed) {
                message.success('Images uploaded to Shopify successfully.');
                setSelectedImagesForUpload([]);
                cancelSelectedComparisonImageModal();
            } else {
                setShopifyUploadError(apiMessage || 'Failed to upload images to Shopify');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to upload images to Shopify';
            setShopifyUploadError(errorMessage);
        } finally {
            setUploadingToShopify(false);
        }
    }

    useEffect(() => {
        if (!visible || !targetProductId) return;

        const checkAndLookup = () => {
            const isAuthorized = localStorage.getItem('shopify_authorized') === 'true';
            if (isAuthorized && targetProductId) {
                ensureProductIdentifiersThenLookup(targetProductId).catch(() => { });
                return true;
            }
            return false;
        };

        if (checkAndLookup()) return;

        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = setInterval(() => {
            retryCount++;
            if (checkAndLookup() || retryCount >= maxRetries) {
                clearInterval(retryInterval);
            }
        }, 500);

        return () => clearInterval(retryInterval);
    }, [visible, targetProductId, productAssetData?.brand_id, productAssetData?.gtin]);

    const getModalTitle = () => {
        if (matchedProduct) {
            return 'Select up to 8 images for the Shopify product';
        }
        if (lookupError && !lookupLoading) {
            return 'Product not found - Search by title';
        }
        return 'Upload Images to Shopify';
    };

    const handleModalCancel = () => {
        if (uploadingToShopify) {
            return;
        }
        cancelSelectedComparisonImageModal();
    };

    return (
        <>
            <Modal
                className="comparison-image-popup"
                destroyOnClose={true}
                closable={!uploadingToShopify}
                maskClosable={!uploadingToShopify}
                title={<span className="manrope f-14 w-600">{getModalTitle()}</span>}
                visible={visible}
                onCancel={handleModalCancel}
                footer={[
                    <>
                        {matchedProduct && (
                            <div className="justify-in-end">
                                <Tooltip title={<span className="manrope f-12 white">Upload to Shopify</span>}>
                                    <span>
                                        <Button className="modal-okay square font-14"
                                                    disabled={
                                                        uploadingToShopify ||
                                                        selectedImagesForUpload.length === 0 ||
                                                        selectedImagesForUpload.some((item) => !item.slot)
                                                    }
                                                    onClick={uploadToShopify}>
                                            Upload {uploadingToShopify && <LoadingOutlined />}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </div>
                        )}
                        {shopifyUploadError && <div className='manrope f-12 red mt-5'>{shopifyUploadError}</div>}
                    </>]}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 16, position: 'relative' }}>
                    {lookupLoading && (
                        <div className="manrope f-14" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Spin size="small" />
                            Looking up product by SKU/UPC...
                        </div>
                    )}
                    {!lookupLoading && matchedProduct && (() => {
                        const shopifySku = matchedProduct?.sku ?? matchedProduct?.variants?.edges?.[0]?.node?.sku ?? matchedProduct?.variants?.[0]?.sku ?? null;
                        const shopifyUpc = matchedProduct?.upc ?? matchedProduct?.barcode ?? matchedProduct?.variants?.edges?.[0]?.node?.barcode ?? matchedProduct?.variants?.[0]?.barcode ?? null;
                        const derivedSku = shopifySku ?? productIdentifiers.sku ?? productAssetData?.brand_id ?? null;
                        const derivedUpc = shopifyUpc ?? productIdentifiers.upc ?? productAssetData?.gtin ?? null;
                        return (
                            <div style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: 8,
                                padding: 20,
                                backgroundColor: '#fafafa',
                                marginBottom: 8
                            }}>
                                <div className="manrope f-14" style={{ display: 'grid', rowGap: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>Title:</span>
                                        <span style={{ color: '#141414' }}>{matchedProduct.title || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>Handle:</span>
                                        <span style={{ color: '#141414' }}>{matchedProduct.handle || '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>SKU:</span>
                                        <span style={{ color: '#141414' }}>{derivedSku || '—'}</span>
                                    </div>
                                    {derivedUpc && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>UPC:</span>
                                            <span style={{ color: '#141414' }}>{derivedUpc}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    {!lookupLoading && !matchedProduct && (
                        <div>
                            <label className="manrope f-14 black-55" style={{ marginBottom: 4 }}>Search by Title</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Input
                                    value={manualSearchTerm}
                                    onChange={(e) => {
                                        setManualSearchTerm(e.target.value);
                                        setHasSearched(false);
                                        if (!e.target.value) {
                                            setManualResults([]);
                                            setSelectedShopifyProduct(null);
                                        }
                                    }}
                                    onPressEnter={handleManualSearch}
                                    placeholder="Enter product title to search"
                                    disabled={uploadingToShopify || manualSearchLoading}
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleManualSearch}
                                    disabled={uploadingToShopify || manualSearchLoading || !manualSearchTerm || manualSearchTerm.length < 2}
                                    loading={manualSearchLoading}
                                >
                                    Search
                                </Button>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {manualSearchLoading ? (
                                    <div className="manrope f-13"><Spin size="small" /> Searching...</div>
                                ) : (
                                    manualResults && manualResults.length > 0 ? (
                                        <List
                                            size="small"
                                            bordered
                                            dataSource={manualResults}
                                            renderItem={item => (
                                                <List.Item
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setSelectedShopifyProduct(item)}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Avatar shape="square" src={item.image || item.featured_image || undefined}>
                                                                {item.title?.[0] || 'P'}
                                                            </Avatar>
                                                        }
                                                        title={<span className="manrope f-13">{item.title || item.handle}</span>}
                                                        description={<span className="manrope f-12 grey-8c">ID: {item.id}{item.handle ? ` • Handle: ${item.handle}` : ''}</span>}
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    ) : hasSearched && !manualSearchLoading ? (
                                        <div className="manrope f-13" style={{ color: '#ff4d4f' }}>Match not found.</div>
                                    ) : null
                                )}
                            </div>
                        </div>
                    )}
                    {matchedProduct && (
                        <Row>
                            <Col md={24} lg={24} xl={24}>
                                <div className='display-flex j-s a-s wrap sa-s gg-16 custom-scroll product-grid-list amz w-100'>
                                    <ImageListTypePreviews
                                        product_images={allProductImages}
                                        selectedImages={selectedImagesForUpload}
                                        toggleImageSelection={toggleImageSelection}
                                        onSlotChange={updateImageSlot}
                                        occupiedSlots={occupiedSlots}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                    {uploadingToShopify && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                padding: 24,
                                textAlign: 'center'
                            }}
                        >
                            <Spin size="large" />
                            <div className="manrope f-14 w-600" style={{ marginTop: 12, color: '#141414' }}>
                                Uploading images to Shopify. Please wait...
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </>)
}

export default ShopifyImageSelectionModal;

